<template>
  <div class="panel">
    <div class="panel-title">Copie des données de base</div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[20rem] w-full"
        :label="'Société de base (locale)'"
        :options="localeOptions"
        :value="selectedLocale"
        item-id-property="id"
        :label-fields="['label']"
        clearable="false"
        @change="(v) => (selectedLocale = v)"
      />

      <hit-select
        class="min-w-[32rem] w-full"
        :label="'Société cible'"
        :options="companyOptions"
        :value="selectedSchemaId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onTargetSchemaChange"
      />
    </div>

    <div v-if="selectedSchemaId" class="mt-4 preview-panel">
      <div class="preview-title">Données existantes dans la société cible</div>
      <div v-if="previewLoading" class="preview-row loading">
        Chargement des informations…
      </div>
      <template v-else-if="previewExisting">
        <div
          v-for="(info, key) in orderedPreview"
          :key="key"
          class="preview-row"
          :class="info.count > 0 ? 'warn' : 'ok'"
        >
          <span class="icon">
            {{ info.count > 0 ? '⚠️' : '✅' }}
          </span>
          <span class="txt">
            <strong>{{ info.description }} :</strong>
            <span v-if="info.count > 0">
              {{ info.count }} enregistrement{{
                info.count > 1 ? 's' : ''
              }}
              existant{{ info.count > 1 ? 's' : '' }}
            </span>
            <span v-else> Aucune donnée existante </span>
          </span>
        </div>
      </template>
      <div v-else class="preview-row loading">
        Impossible de récupérer les données existantes.
      </div>
    </div>

    <div class="mt-4 risk-panel">
      <div class="risk-title">Ce que cette opération va faire</div>
      <ul class="risk-list">
        <li>
          <span class="bullet">1</span>
          <span class="text">
            Copier les <strong>codes TVA</strong>, <strong>journaux</strong> et
            <strong>catégories de ressources</strong>.
          </span>
        </li>
        <li>
          <span class="bullet">2</span>
          <span class="text">
            Copier les <strong>ressources</strong>,
            <strong>délais de paiement</strong>, <strong>adresses</strong> (hors
            adresse Peppol par défaut) et <strong>équipements</strong>.
          </span>
        </li>
        <li>
          <span class="bullet">3</span>
          <span class="text">
            Aligner la <strong>configuration Peppol</strong> (mapping TVA) de la
            société cible sur celle de la société de base.
          </span>
        </li>
      </ul>
      <div class="risk-warning">
        <span class="dot" />
        <span>
          Des données déjà présentes dans la société cible peuvent être
          <strong>complétées</strong> ou <strong>écrasées</strong>.
        </span>
      </div>

      <label class="confirm-row" for="copy-base-data-confirm">
        <input
          id="copy-base-data-confirm"
          v-model="confirmed"
          type="checkbox"
          class="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span>
          Je comprends les conséquences de cette copie et je souhaite continuer.
        </span>
      </label>
    </div>

    <div class="mt-4 flex gap-2">
      <hit-button
        :label="'Importer les données de base'"
        color="accent"
        prefix-icon="play_arrow"
        :disabled="
          running || !selectedLocale || !selectedSchemaId || !confirmed
        "
        @click="run"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'ScriptCopyBaseData',
  components: {HitButton, HitSelect},
  data() {
    return {
      localesFromApi: [],
      selectedLocale: 'fr-be',
      companyOptions: [],
      selectedSchemaId: null,
      confirmed: false,
      running: false,
      result: null,
      previewLoading: false,
      previewExisting: null,
    };
  },
  computed: {
    localeOptions() {
      if (this.localesFromApi && this.localesFromApi.length) {
        return this.localesFromApi;
      }
      return [
        {id: 'fr-be', label: 'Français/Belgique'},
        {id: 'en-gb', label: 'Anglais/Grande-Bretagne'},
      ];
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
    orderedPreview() {
      if (!this.previewExisting) return {};
      const order = [
        'tax_code',
        'resource_category',
        'resource',
        'journal',
        'due_date',
        'address',
        'equipment',
      ];
      const out = {};
      order.forEach((k) => {
        if (this.previewExisting[k]) out[k] = this.previewExisting[k];
      });
      // add any other keys at the end
      Object.keys(this.previewExisting).forEach((k) => {
        if (!out[k]) out[k] = this.previewExisting[k];
      });
      return out;
    },
  },
  async mounted() {
    await this.loadScriptsMeta();
    await this.loadCompanyOptions();
    if (this.selectedSchemaId) {
      await this.fetchPreview();
    }
  },
  methods: {
    async loadScriptsMeta() {
      try {
        const scripts = await adminApi.listScripts();
        const script = (scripts || []).find((s) => s.id === 'copy_base_data');
        this.localesFromApi = script?.params?.locales || [];
      } catch {
        this.localesFromApi = [];
      }
    },
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 500});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.schema_id || r.id, label: label || r.id};
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onTargetSchemaChange(value) {
      this.selectedSchemaId = value;
      this.result = null;
      this.fetchPreview();
    },
    async fetchPreview() {
      const schema = this.selectedSchemaId;
      if (!schema) {
        this.previewExisting = null;
        this.previewLoading = false;
        return;
      }
      this.previewLoading = true;
      this.previewExisting = null;
      try {
        const res = await adminApi.getCopyBaseDataPreview(schema);
        this.previewExisting = res?.existing || null;
      } catch {
        this.previewExisting = null;
      } finally {
        if (this.selectedSchemaId === schema) {
          this.previewLoading = false;
        }
      }
    },
    async run() {
      if (!this.selectedLocale || !this.selectedSchemaId) return;
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.runScript('/scripts/copy_base_data/run', {
          locale: this.selectedLocale,
          target_schema: this.selectedSchemaId,
          force: true,
        });
        this.result = res;
        await this.fetchPreview();
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.82);
  padding: 0.9rem;
}

.panel-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  margin-bottom: 0.75rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.preview-panel {
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04),
    0 10px 24px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: rgba(15, 23, 42, 0.9);
}

.preview-title {
  font-weight: 700;
  margin-bottom: 0.1rem;
}

.preview-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.4rem;
  border-radius: 0.5rem;
}

.preview-row.ok {
  background: color-mix(in srgb, var(--color-accent, #00997c) 14%, #ffffff);
}

.preview-row.warn {
  background: rgba(249, 115, 22, 0.12);
}

.preview-row.loading {
  opacity: 0.85;
  font-style: italic;
}

.preview-row .icon {
  flex: 0 0 auto;
}

.preview-row .txt {
  flex: 1 1 auto;
}

.risk-panel {
  margin-top: 1rem;
  padding: 0.6rem 0.85rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(248, 250, 252, 0.96);
  display: grid;
  gap: 0.35rem;
  font-size: 0.88rem;
  color: rgba(15, 23, 42, 0.85);
}

.risk-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.9);
}

.risk-list {
  margin: 0.1rem 0 0;
  padding: 0;
  display: grid;
  gap: 0.15rem;
  list-style: none;
}

.risk-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}

.risk-list .bullet {
  flex: 0 0 auto;
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  background: rgba(148, 163, 184, 0.18);
}

.risk-list .text {
  flex: 1 1 auto;
}

.risk-warning {
  margin-top: 0.35rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: rgba(180, 83, 9, 1);
}

.risk-warning .dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(234, 88, 12, 1);
  box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.14);
}

.confirm-row {
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: rgba(55, 65, 81, 1);
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
